// client/src/components/secure/CeRecords.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser, canSeePersonFile } from '../../hooks/useCurrentUser';

const CATEGORIES = ['Core', 'Elective', 'Other'];

// Log continuing-education credits against an agent or an employee.
// Pass exactly one of agentId / employeeId.
const CeRecords = ({ agentId, employeeId }) => {
    const [records, setRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formData, setFormData] = useState({ course_name: '', provider: '', category: 'Elective', hours: '', completion_date: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    const fetchRecords = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams();
        if (agentId) params.set('agent_id', agentId);
        if (employeeId) params.set('employee_id', employeeId);
        apiClient.get(`/api/ce?${params.toString()}`)
            .then(data => setRecords(data))
            .catch(err => setError(err.message || 'Failed to load CE records.'))
            .finally(() => setIsLoading(false));
    }, [agentId, employeeId, apiClient]);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    const totalHours = records.reduce((sum, r) => sum + Number(r.hours), 0);

    const openForm = () => {
        setFormData({ course_name: '', provider: '', category: 'Elective', hours: '', completion_date: '' });
        setIsFormOpen(true);
    };

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            await apiClient.post('/api/ce', {
                ...formData,
                agent_id: agentId || null,
                employee_id: employeeId || null,
            });
            setIsFormOpen(false);
            fetchRecords();
        } catch (err) {
            setError(err.message || 'Failed to log CE record.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white p-8 rounded-lg shadow mt-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-800">Continuing Education</h2>
                {/* The person, the office manager or the managing broker logs credit (server rule in routes/ce.js). */}
                {canSeePersonFile(getCurrentUser(), { agentId, employeeId }) && getCurrentUser()?.role !== 'examiner' && (
                    <button onClick={openForm} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900">
                        Log CE Credit
                    </button>
                )}
            </div>

            {!isLoading && <p className="text-sm text-slate-500 mb-4">Total hours on file: <span className="font-semibold text-slate-700">{totalHours}</span></p>}

            {isFormOpen && (
                <form onSubmit={handleSubmit} className="bg-slate-50 p-4 rounded-lg mb-6 space-y-3">
                    <input name="course_name" value={formData.course_name} onChange={handleChange} placeholder="Course name" required className="p-2 border rounded w-full" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input name="provider" value={formData.provider} onChange={handleChange} placeholder="Provider (optional)" className="p-2 border rounded" />
                        <select name="category" value={formData.category} onChange={handleChange} className="p-2 border rounded">
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input type="number" step="0.5" min="0" name="hours" value={formData.hours} onChange={handleChange} placeholder="Hours" required className="p-2 border rounded" />
                    </div>
                    <input type="date" name="completion_date" value={formData.completion_date} onChange={handleChange} required className="p-2 border rounded w-full" />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-3">
                        <button type="button" onClick={() => setIsFormOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            )}

            {isLoading && <p className="text-slate-500">Loading CE records...</p>}
            {!isLoading && error && !isFormOpen && <p className="text-red-500">{error}</p>}
            {!isLoading && (
                <ul className="divide-y divide-gray-200">
                    {records.map(record => (
                        <li key={record.ce_record_id} className="py-3 flex justify-between items-baseline">
                            <div>
                                <span className="font-semibold text-slate-800">{record.course_name}</span>
                                <span className="text-slate-500"> — {record.category}</span>
                                {record.provider && <span className="text-slate-400 text-sm"> ({record.provider})</span>}
                            </div>
                            <div className="text-sm text-slate-500 text-right">
                                <div>{record.hours} hrs</div>
                                <div>{new Date(record.completion_date).toLocaleDateString()}</div>
                            </div>
                        </li>
                    ))}
                    {records.length === 0 && <p className="text-slate-500 py-4">No CE credits logged yet.</p>}
                </ul>
            )}
        </div>
    );
};

export default CeRecords;
